/**
 * Inline AI review mode.
 *
 * Renders an AI suggestion as an in-editor diff and drives a floating dock that
 * lets the user step through each change and Accept/Reject it, toggle between
 * Diff and Preview, or Skip / Apply all remaining changes at once.
 *
 * Review is block-level: each changed paragraph (or list/table) is one
 * accept/reject unit, shown with inline word-coloring (struck red / green) so
 * the user sees what changed. The applied result is assembled from whole
 * verbatim blocks, so accepting/rejecting can never mangle whitespace.
 *
 * The diff markup (<span class="air-del/air-ins">) is written straight into the
 * editor body via DOM so it survives the editor's restrictive `valid_elements`
 * (which would otherwise strip it). The editor is made non-editable while
 * reviewing; on finish, the chosen result is written back through
 * `editor.setContent`, which reparses and cleans away all review markup.
 */

import { tokenize, diffTokens, isTag, isWhitespace } from "./diff";

let active = false;

export function isReviewing() {
    return active;
}

const REVIEW_STYLES = `
.air-del { background:#fde8e7; color:#b3261e; text-decoration:line-through; border-radius:2px; padding:0 1px; }
.air-ins { background:#e6f4ea; color:#1e7e34; border-radius:2px; padding:0 1px; }
.air-change { cursor:pointer; }
.air-change.air-current { background:#eef4ff; border-left:3px solid #2f6df6; margin-left:-11px; padding-left:8px; border-radius:3px; }
`;

const DOCK_CSS = `
.air-dock { display:flex; align-items:center; justify-content:space-between; gap:14px;
    margin-top:8px; width:100%; box-sizing:border-box; background:#fff; border:1px solid #e1e3e6;
    border-radius:10px; box-shadow:0 2px 10px rgba(0,0,0,.06); padding:8px 14px;
    font-family:'Open Sans',Arial,sans-serif; font-size:13px; color:#1f2329; }
.air-dock .air-group { display:flex; align-items:center; gap:6px; }
.air-dock button { cursor:pointer; border:1px solid transparent; background:transparent; border-radius:6px;
    padding:5px 9px; font-size:13px; line-height:1; color:#1f2329; display:inline-flex; align-items:center; gap:5px; }
.air-dock button:hover { background:#f1f3f5; }
.air-dock .air-counter { min-width:54px; text-align:center; color:#5f6368; font-variant-numeric:tabular-nums; }
.air-dock .air-accept { color:#1e7e34; }
.air-dock .air-reject { color:#b3261e; }
.air-dock .air-toggle.is-active { background:#eef2ff; border-color:#c7d2fe; color:#2f6df6; font-weight:600; }
.air-dock .air-primary { background:#2f6df6; color:#fff; font-weight:600; }
.air-dock .air-primary:hover { background:#255ad6; }
`;

let dockStyleEl = null;

function ensureDockStyles() {
    if (dockStyleEl) {
        return;
    }
    dockStyleEl = document.createElement("style");
    dockStyleEl.textContent = DOCK_CSS;
    document.head.appendChild(dockStyleEl);
}

/**
 * Tag each non-equal op with a change id, grouping a contiguous run of
 * deletions/insertions into a single reviewable change.
 */
/**
 * Split an HTML body into top-level blocks (paragraphs, lists, tables, ...).
 * Review happens one block at a time so a reworded paragraph is a single
 * accept/reject rather than a dozen word-level ones, and the applied result is
 * assembled from whole verbatim blocks so whitespace can't get mangled.
 */
function splitBlocks(html) {
    const doc = new DOMParser().parseFromString(html, "text/html");
    const blocks = [];
    doc.body.childNodes.forEach((node) => {
        if (node.nodeType === Node.ELEMENT_NODE) {
            if (isMeaningfulBlock(node)) {
                blocks.push(node.outerHTML);
            }
        } else if (node.nodeType === Node.TEXT_NODE && node.textContent.trim()) {
            blocks.push(`<p>${node.textContent}</p>`);
        }
    });
    return blocks;
}

/**
 * A block is meaningful if it has visible text or carries media — empty
 * paragraphs (including `<p><br></p>` / `<p>&nbsp;</p>`) are dropped so they
 * don't show up as changes or leave stray blank lines in the applied result.
 */
function isMeaningfulBlock(el) {
    if (el.querySelector("img, table, hr")) {
        return true;
    }
    return el.textContent.replace(/\u00a0/g, " ").trim() !== "";
}

/**
 * The comparable identity of a block: its visible text, whitespace-collapsed.
 * Two blocks with the same key are treated as unchanged even if their markup
 * was reformatted.
 */
function blockKey(html) {
    const doc = new DOMParser().parseFromString(html, "text/html");
    return doc.body.textContent.replace(/\s+/g, " ").trim();
}

const SIMILARITY_THRESHOLD = 0.3;

/** Jaccard similarity of the word sets of two block texts (0..1). */
function blockSimilarity(aText, bText) {
    const a = new Set(aText.toLowerCase().match(/[a-z0-9']+/g) || []);
    const b = new Set(bText.toLowerCase().match(/[a-z0-9']+/g) || []);
    if (a.size === 0 && b.size === 0) {
        return 1;
    }
    let intersection = 0;
    a.forEach((word) => {
        if (b.has(word)) {
            intersection++;
        }
    });
    return intersection / (a.size + b.size - intersection);
}

/**
 * Align old vs new blocks by similarity (Needleman-Wunsch) and produce an
 * ordered list of segments:
 *   { type: 'eq', html }                        — identical block
 *   { type: 'chg', cid, oldHtml, newHtml }      — one reviewable change
 *
 * A block that's similar enough before/after (e.g. a paragraph with a typo
 * fixed) aligns 1:1 and becomes its own change, so editing every paragraph
 * yields one change per paragraph. Dissimilar blocks can't substitute, so runs
 * of pure insertions/deletions (e.g. many paragraphs condensed into one) group
 * into a single change.
 */
function diffBlocks(oldBlocks, newBlocks) {
    const ak = oldBlocks.map(blockKey);
    const bk = newBlocks.map(blockKey);
    const n = oldBlocks.length;
    const m = newBlocks.length;
    const EQ = 3;
    const SUB = 1.5;
    const GAP = -1;

    const diagReward = (i, j) => {
        if (ak[i] === bk[j]) {
            return EQ;
        }
        const sim = blockSimilarity(ak[i], bk[j]);
        return sim >= SIMILARITY_THRESHOLD ? SUB + sim : -Infinity;
    };

    const dp = Array.from({ length: n + 1 }, () => new Array(m + 1).fill(0));
    for (let i = 1; i <= n; i++) {
        dp[i][0] = dp[i - 1][0] + GAP;
    }
    for (let j = 1; j <= m; j++) {
        dp[0][j] = dp[0][j - 1] + GAP;
    }
    for (let i = 1; i <= n; i++) {
        for (let j = 1; j <= m; j++) {
            const diag = diagReward(i - 1, j - 1);
            dp[i][j] = Math.max(
                diag === -Infinity ? -Infinity : dp[i - 1][j - 1] + diag,
                dp[i - 1][j] + GAP,
                dp[i][j - 1] + GAP
            );
        }
    }

    // Trace back into ordered ops.
    const ops = [];
    let i = n;
    let j = m;
    while (i > 0 && j > 0) {
        const diag = diagReward(i - 1, j - 1);
        if (diag !== -Infinity && dp[i][j] === dp[i - 1][j - 1] + diag) {
            ops.push({ type: ak[i - 1] === bk[j - 1] ? "eq" : "sub", oi: i - 1, nj: j - 1 });
            i--;
            j--;
        } else if (dp[i][j] === dp[i - 1][j] + GAP) {
            ops.push({ type: "del", oi: i - 1 });
            i--;
        } else {
            ops.push({ type: "ins", nj: j - 1 });
            j--;
        }
    }
    while (i > 0) {
        ops.push({ type: "del", oi: --i });
    }
    while (j > 0) {
        ops.push({ type: "ins", nj: --j });
    }
    ops.reverse();

    // Build segments: subs are individual changes; runs of pure del/ins group.
    const segments = [];
    let cid = 0;
    let pendingOld = [];
    let pendingNew = [];
    const flush = () => {
        if (pendingOld.length === 0 && pendingNew.length === 0) {
            return;
        }
        segments.push({
            type: "chg",
            cid: ++cid,
            oldHtml: pendingOld.join(""),
            newHtml: pendingNew.join(""),
        });
        pendingOld = [];
        pendingNew = [];
    };

    for (const op of ops) {
        if (op.type === "eq") {
            flush();
            segments.push({ type: "eq", html: oldBlocks[op.oi] });
        } else if (op.type === "sub") {
            flush();
            segments.push({
                type: "chg",
                cid: ++cid,
                oldHtml: oldBlocks[op.oi],
                newHtml: newBlocks[op.nj],
            });
        } else if (op.type === "del") {
            pendingOld.push(oldBlocks[op.oi]);
        } else {
            pendingNew.push(newBlocks[op.nj]);
        }
    }
    flush();
    return segments;
}

/**
 * Inline word-level diff of one changed block, for display only: unchanged
 * words plain, removed words struck red, added words green. Tags and whitespace
 * pass through untouched. The enclosing change `<div>` (not the words) carries
 * selection/current state.
 */
function inlineWordDiff(oldHtml, newHtml) {
    const ops = diffTokens(tokenize(oldHtml), tokenize(newHtml));
    let out = "";
    for (const op of ops) {
        const token = op.token;
        if (op.type === "eq" || isTag(token) || isWhitespace(token)) {
            out += token;
            continue;
        }
        const cls = op.type === "del" ? "air-del" : "air-ins";
        out += `<span class="${cls}">${token}</span>`;
    }
    return out;
}

export function startReview(editor, { bodyOld, bodyNew, onResolve }) {
    if (active) {
        return;
    }

    const segments = diffBlocks(splitBlocks(bodyOld), splitBlocks(bodyNew));
    const changeIds = segments
        .filter((segment) => segment.type === "chg")
        .map((segment) => segment.cid);

    if (changeIds.length === 0) {
        // Defensive: if a prior review left diff markup in the body, restore
        // the clean content so a no-op result can't surface a stale diff.
        if (editor.getBody().querySelector(".air-change, .air-del, .air-ins")) {
            editor.setContent(bodyOld);
        }
        editor.notificationManager.open({
            type: "info",
            text: "The AI didn't suggest any changes.",
            timeout: 2500,
        });
        return;
    }

    active = true;

    const decisions = {};
    changeIds.forEach((id) => {
        decisions[id] = "pending";
    });
    let mode = "diff";
    let currentIndex = 0;

    const body = editor.getBody();
    body.setAttribute("contenteditable", "false");
    editor.dom.addStyle(REVIEW_STYLES);
    ensureDockStyles();

    const currentCid = () => changeIds[currentIndex];
    const pendingCount = () =>
        changeIds.filter((id) => decisions[id] === "pending").length;

    /* ---- rendering -------------------------------------------------- */

    function renderDiffHtml() {
        let out = "";
        for (const segment of segments) {
            if (segment.type === "eq") {
                out += segment.html;
                continue;
            }
            const decision = decisions[segment.cid];
            let inner;
            if (decision === "accept") {
                inner = segment.newHtml;
            } else if (decision === "reject") {
                inner = segment.oldHtml;
            } else {
                inner = inlineWordDiff(segment.oldHtml, segment.newHtml);
            }
            const current = segment.cid === currentCid() ? " air-current" : "";
            out += `<div class="air-change${current}" data-cid="${segment.cid}">${inner}</div>`;
        }
        return out;
    }

    function buildResultHtml(pendingAs) {
        let out = "";
        for (const segment of segments) {
            if (segment.type === "eq") {
                out += segment.html;
                continue;
            }
            let decision = decisions[segment.cid];
            if (decision === "pending") {
                decision = pendingAs;
            }
            out += decision === "reject" ? segment.oldHtml : segment.newHtml;
        }
        return out;
    }

    function render() {
        if (mode === "diff") {
            body.innerHTML = renderDiffHtml();
            body.querySelectorAll(".air-change").forEach((el) => {
                el.addEventListener("click", () => {
                    const idx = changeIds.indexOf(Number(el.dataset.cid));
                    if (idx >= 0) {
                        currentIndex = idx;
                        render();
                    }
                });
            });
            const cur = body.querySelector(".air-current");
            if (cur) {
                cur.scrollIntoView({ block: "nearest" });
            }
        } else {
            body.innerHTML = buildResultHtml("accept");
        }
        updateDock();
    }

    /* ---- dock ------------------------------------------------------- */

    const dock = document.createElement("div");
    dock.className = "air-dock";
    dock.innerHTML = `
        <div class="air-group">
            <button type="button" data-action="mode" class="air-toggle is-active">Diff mode</button>
            <button type="button" data-action="mode-preview" class="air-toggle">Preview</button>
        </div>
        <div class="air-group">
            <button type="button" data-action="prev" title="Previous change">&#9650;</button>
            <span class="air-counter"></span>
            <button type="button" data-action="next" title="Next change">&#9660;</button>
            <button type="button" data-action="reject" class="air-reject" title="Reject change">&#10005;</button>
            <button type="button" data-action="accept" class="air-accept" title="Accept change">&#10003;</button>
        </div>
        <div class="air-group">
            <button type="button" data-action="skip">Skip remaining</button>
            <button type="button" data-action="apply" class="air-primary"></button>
        </div>
    `;
    editor.getContainer().insertAdjacentElement("afterend", dock);

    const counterEl = dock.querySelector(".air-counter");
    const applyEl = dock.querySelector('[data-action="apply"]');
    const diffBtn = dock.querySelector('[data-action="mode"]');
    const previewBtn = dock.querySelector('[data-action="mode-preview"]');

    function updateDock() {
        counterEl.textContent = `${currentIndex + 1} / ${changeIds.length}`;
        applyEl.textContent = `Apply remaining (${pendingCount()})`;
        diffBtn.classList.toggle("is-active", mode === "diff");
        previewBtn.classList.toggle("is-active", mode === "preview");
    }

    function gotoNextPending() {
        for (let step = 1; step <= changeIds.length; step++) {
            const idx = (currentIndex + step) % changeIds.length;
            if (decisions[changeIds[idx]] === "pending") {
                currentIndex = idx;
                return;
            }
        }
    }

    function decide(decision) {
        decisions[currentCid()] = decision;
        if (pendingCount() === 0) {
            finish();
            return;
        }
        gotoNextPending();
        render();
    }

    function finishRemaining(decision) {
        changeIds.forEach((id) => {
            if (decisions[id] === "pending") {
                decisions[id] = decision;
            }
        });
        finish();
    }

    function finish() {
        const result = buildResultHtml("accept");
        body.setAttribute("contenteditable", "true");
        editor.undoManager.transact(() => editor.setContent(result));
        dock.remove();
        active = false;

        if (typeof onResolve === "function") {
            const accepted = changeIds.filter(
                (id) => decisions[id] === "accept"
            ).length;
            const rejected = changeIds.length - accepted;
            onResolve({
                outcome:
                    accepted === 0
                        ? "rejected"
                        : rejected === 0
                          ? "accepted"
                          : "partial",
                changes_total: changeIds.length,
                changes_accepted: accepted,
                changes_rejected: rejected,
            });
        }

        editor.notificationManager.open({
            type: "success",
            text: "AI changes applied.",
            timeout: 2000,
        });
    }

    dock.addEventListener("click", (event) => {
        const button = event.target.closest("button");
        if (!button) {
            return;
        }
        event.preventDefault();
        switch (button.dataset.action) {
            case "mode":
                mode = "diff";
                render();
                break;
            case "mode-preview":
                mode = "preview";
                render();
                break;
            case "prev":
                currentIndex =
                    (currentIndex - 1 + changeIds.length) % changeIds.length;
                render();
                break;
            case "next":
                currentIndex = (currentIndex + 1) % changeIds.length;
                render();
                break;
            case "accept":
                decide("accept");
                break;
            case "reject":
                decide("reject");
                break;
            case "skip":
                finishRemaining("reject");
                break;
            case "apply":
                finishRemaining("accept");
                break;
        }
    });

    render();
}
